import React, { useEffect, useState } from "react";
import API from "../../../Utils/api";
import toast from "react-hot-toast";
import CarrierCardSkeleton from "./CarrierCardSkeleton";
import CarrierCard from "./CarrierCard";
import CarrierViewModal from "./CarrierViewModal";
import EditCarrierModal from "./EditCarrierModal";

const ITEMS_PER_PAGE = 8;

const AllCarrier = ({ refresh, onRefresh }) => {
  const [carrier, setCarrier] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewCarrier, setViewCarrier] = useState(null);
  const [editcarrier, setEditCarrier] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchGalley = async () => {
    setLoading(true);
    try {
      const res = await API.get(
        `/carrier/get-all?page=${currentPage}&limit=${ITEMS_PER_PAGE}&search=${search}`,
      );

      setCarrier(res.data?.carrier || []);
      setTotalPages(res.data?.totalPages || 1);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error("Error fetching carrier:", error);
      toast.error("Failed to load carrier");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setCurrentPage(1);
      fetchGalley();
    }, 500);

    return () => clearTimeout(delay);
  }, [search]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/carrier/delete/${deleteId}`);
      setDeleteId(null);
      onRefresh();
      toast.success("Your carrier has been Deleted successfully! 🎉");
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <>
      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search carrier by heading..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
          />
        </div>
        {search && (
          <button
            onClick={() => setSearch("")}
            className="px-4 py-2.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 text-sm transition"
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Results Count */}
      {!loading && (
        <p className="text-sm text-gray-500 mb-4">
          Showing <span className="font-semibold text-gray-700">{total}</span>{" "}
          carrier
        </p>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
              <CarrierCardSkeleton key={i} />
            ))
          : carrier.map((carrier) => (
              <CarrierCard
                key={carrier._id}
                carrier={carrier}
                onView={() => setViewCarrier(carrier)}
                onEdit={() => setEditCarrier(carrier)}
                onDelete={() => setDeleteId(carrier._id)}
              />
            ))}
      </div>

      {/* Empty State */}
      {!loading && total === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <span className="text-6xl mb-4">📝</span>
          <p className="text-lg font-medium">No carrier found</p>
          <p className="text-sm">
            Try adjusting your search or add a new carrier
          </p>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-10">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`w-9 h-9 rounded-lg font-medium transition ${
                currentPage === i + 1
                  ? "bg-green-600 text-white"
                  : "border border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            Next →
          </button>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="text-center">
              <span className="text-5xl">🗑️</span>
              <h3 className="text-lg font-bold text-gray-800 mt-3">
                Delete Carrier?
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewCarrier && (
        <CarrierViewModal
          carrier={viewCarrier}
          onClose={() => setViewCarrier(null)}
        />
      )}

      {/* Edit Modal */}
      {editcarrier && (
        <EditCarrierModal
          carrier={editcarrier}
          onClose={() => setEditCarrier(null)}
          onSuccess={() => {
            setEditCarrier(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default AllCarrier;
